<script setup lang="ts">
import type { SubjectCode } from '~/models/Subject'
import type { SupportProductName } from '~/models/Product'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { faBook, faHeadphones } from '@fortawesome/duotone-regular-svg-icons'
import { isAlwaysIncluded, sortBySubjectIndex } from '~/utils/subjectSorter'
import arrayUtils from '~/utils/arrayUtils'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useText from '~/composables/useText'
import Button from '~/components/ui/Button.vue'
import SupportProductModal from '~/components/home/SupportProductModal.vue'
import DefaultIllustration from '~/components/home/DefaultIllustration.vue'
import DefaultGreetings from '~/components/home/DefaultGreetings.vue'

const { t } = useI18n()
const { capitalize } = useText()
const { unique } = arrayUtils()
const { resolveBaseUrl } = useUrlBuilder()
const { user } = storeToRefs(useAuthStore())

const {
  filteredSubjectCodes,
  filteredAddonProducts,
  filteredSupportProducts,
  filteredSupportProductNames,
  selectedSupportProductName,
} = storeToRefs(useProductStore())

const withExtraSubjects = computed(() => filteredSubjectCodes.value.length > 0
  ? [...filteredSubjectCodes.value, ...isAlwaysIncluded].filter(unique<SubjectCode>).sort(sortBySubjectIndex)
  : []
)

const mapSupportIcon = (name: SupportProductName) => ({
  bokstøtte: faBook,
  lydstøtte: faHeadphones,
}[name])
</script>

<template>
  <section class="bg-green-40 relative mb-12 flex-col justify-center text-white">
    <div class="max-w-screen-au xs:pb-14 relative z-20 mx-auto size-full px-4 pt-40 pb-10 transition-all sm:px-8 sm:pt-28">
      <h1
        class="sr-only"
        v-text="t('home.title')"
      />
      <DefaultGreetings
        v-if="user"
        :name="user.fullName"
      />
      <p
        v-if="withExtraSubjects.length"
        class="mt-4 mb-2 font-medium"
        v-text="t('home.yourSubjects')"
      />
      <ul
        v-if="withExtraSubjects.length"
        class="
          xs:flex-wrap xs:overflow-x-visible xs:pb-0 relative z-10 -mx-4 flex
          h-fit gap-3 overflow-x-scroll px-4 pb-4 md:max-w-[70%]
        "
      >
        <li
          v-for="subjectCode of withExtraSubjects"
          :key="subjectCode"
        >
          <Button
            :to="{ name: 'subject', params: { subject: subjectCode.toLowerCase() } }"
            color="white"
            size="lg"
          >
            {{ t(`metadata.subjects.${subjectCode}`) }}
          </Button>
        </li>
      </ul>
      <p
        v-if="filteredAddonProducts.length || filteredSupportProducts.length"
        class="mt-6 mb-2 font-medium"
        v-text="t('home.yourAddons')"
      />
      <ul
        v-if="filteredAddonProducts.length || filteredSupportProducts.length"
        class="xs:flex-wrap xs:overflow-x-visible xs:pb-0 relative z-10 -mx-4 flex h-fit gap-3 overflow-x-scroll px-4 pb-4 md:max-w-[70%]"
      >
        <li
          v-for="addon in filteredAddonProducts"
          :key="addon.ean"
        >
          <Button
            :to="{ path: resolveBaseUrl(addon) }"
            color="white"
            size="lg"
          >
            {{ addon.aunivers.name }}
          </Button>
        </li>
        <li
          v-for="name of filteredSupportProductNames"
          :key="name"
        >
          <Button
            color="white"
            size="lg"
            @click="selectedSupportProductName = name"
          >
            <font-awesome-icon :icon="mapSupportIcon(name)" />
            {{ capitalize(name) }}
          </Button>

          <SupportProductModal
            :name="name"
            :products="filteredSupportProducts"
            :subjects="filteredSubjectCodes"
            @close="selectedSupportProductName = undefined"
          />
        </li>
      </ul>
      <DefaultIllustration />
    </div>
  </section>
</template>
